import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button } from '@mui/material'
import ScrollToggle from 'components/ScrollToggle/ScrollToggle'
import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFOptionList,
  PDFRadioGroup,
  PDFTextField,
} from 'pdf-lib'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import { useSelector } from 'react-redux'
import { Navigate, useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { lastYears } from 'resources/functions'
import { useFourThreeThreeBApi } from 'resources/hooks/api/fourThreeThreeBApiHook'
import { getBranch, getUser } from 'store/user/selectors'
import { preFillAnyForm } from './FormHelper'
import publishBudgetIcon from '../../../assets/images/billing-icon-w.svg'
import SAlert from 'components/SAlert'
import toast from 'react-hot-toast'
import { useNavigationBlocker } from '../taxHooks/useNavigationBlocker'
import { NavigationBlockerDialog } from '../components/NavigationBlockerDialog'
import { FormsListEng, FormsListSn } from '../formConfig/FormsList'
import { URLS } from 'resources/constants'
import PdfLoadingView from '../components/PdfLoadingView'
import PdfErrorView from '../components/PdfErrorView'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

const PAGE_WIDTH = 800

// This component used for below listed forms only
const FORM_TITLES: Record<string, string> = {
  f433a: 'Form 433-A',
  f433aoi: 'Form 433-A (OIC)',
  f433boi: 'Form 433-B (OIC)',
  f433d: 'Form 433-D',
  f433f: 'Form 433-F',
  p5059: 'Publication 5059',
  p1854: 'Publication 1854',
}

const FORM_TITLE_KEYS: Record<string, string> = {
  f433a: 'f433a',
  f433aoi: 'f433a_oic',
  f433boi: 'f433b_oic',
  f433d: 'f433d',
  f433f: 'f433f',
  p5059: 'p5059',
  p1854: 'p1854',
}

const normalizePdfUrl = (url: string): string => {
  if (!url) return url
  if (url.includes('/files/?p=') && !url.includes('raw=1')) {
    return url.includes('?') ? `${url}&raw=1` : `${url}?raw=1`
  }
  return url
}

const getFieldTypeString = (field: any): string => {
  if (field instanceof PDFTextField) return 'PDFTextField'
  if (field instanceof PDFCheckBox) return 'PDFCheckBox'
  if (field instanceof PDFRadioGroup) return 'PDFRadioGroup'
  if (field instanceof PDFDropdown) return 'PDFDropdown'
  if (field instanceof PDFOptionList) return 'PDFOptionList'
  return 'Unknown'
}
const VALID_FORM_TYPES = new Set(Object.keys(FORM_TITLES))

export default function TaxForms() {
  const [searchParams] = useSearchParams()
  const formType = searchParams.get('form') || 'f433a'

  const { t } = useTranslation('scenes.TaxResolutions.index')
  const branchData = useSelector(getBranch)

  const getFormTitle = useCallback(
    (type: string) => {
      const key = FORM_TITLE_KEYS[type]
      if (key) {
        const translated = t(key)
        if (translated !== key) return translated
      }
      return FORM_TITLES[type] || t('tax_form')
    },
    [t]
  )

  const pdfDocRef = useRef<PDFDocument | null>(null)
  const formRef = useRef<any>(null)
  const masterBytesRef = useRef<Uint8Array | null>(null)
  const generatedPeriodRef = useRef<{
    month: string
    year: string
  } | null>(null)
  const detailsRef = useRef<any | null>(null)
  const loadTokenRef = useRef(0)
  const userData = useSelector(getUser)
  const [documentFile, setDocumentFile] = useState<{ data: Uint8Array } | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [fieldDefs, setFieldDefs] = useState<any[]>([])
  const [values, setValues] = useState<Record<string, any>>({})
  const [pageMeta, setPageMeta] = useState<Record<number, any>>({})
  const [revision, setRevision] = useState<string | null>(null)
  const [isLatestLoaded, setIsLatestLoaded] = useState<boolean>(false)
  const [newerVersionAvailable, setNewerVersionAvailable] = useState<boolean>(false)
  const [isPublished, setIsPublished] = useState<boolean>(false)
  const [publishAlert, setPublishAlert] = useState<boolean>(false)
  const [zoom] = useState<number>(1)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [isDirty, setIsDirty] = useState(false)

  const { showPrompt, confirmNavigation, cancelNavigation } = useNavigationBlocker(isDirty)

  const months = [
    t('month.01', { ns: 'resources.form-validator' }),
    t('month.02', { ns: 'resources.form-validator' }),
    t('month.03', { ns: 'resources.form-validator' }),
    t('month.04', { ns: 'resources.form-validator' }),
    t('month.05', { ns: 'resources.form-validator' }),
    t('month.06', { ns: 'resources.form-validator' }),
    t('month.07', { ns: 'resources.form-validator' }),
    t('month.08', { ns: 'resources.form-validator' }),
    t('month.09', { ns: 'resources.form-validator' }),
    t('month.10', { ns: 'resources.form-validator' }),
    t('month.11', { ns: 'resources.form-validator' }),
    t('month.12', { ns: 'resources.form-validator' }),
  ]

  const monthList = [
    { value: '01', label: t('month.01', { ns: 'resources.form-validator' }) },
    { value: '02', label: t('month.02', { ns: 'resources.form-validator' }) },
    { value: '03', label: t('month.03', { ns: 'resources.form-validator' }) },
    { value: '04', label: t('month.04', { ns: 'resources.form-validator' }) },
    { value: '05', label: t('month.05', { ns: 'resources.form-validator' }) },
    { value: '06', label: t('month.06', { ns: 'resources.form-validator' }) },
    { value: '07', label: t('month.07', { ns: 'resources.form-validator' }) },
    { value: '08', label: t('month.08', { ns: 'resources.form-validator' }) },
    { value: '09', label: t('month.09', { ns: 'resources.form-validator' }) },
    { value: '10', label: t('month.10', { ns: 'resources.form-validator' }) },
    { value: '11', label: t('month.11', { ns: 'resources.form-validator' }) },
    { value: '12', label: t('month.12', { ns: 'resources.form-validator' }) },
  ]

  const currentMonthIndex = new Date().getMonth()
  const currentMonthName = months[currentMonthIndex]

  const [filterParams, setFilterParams] = useState({
    month: { value: currentMonthIndex + 1, label: currentMonthName },
    year: {
      value: new Date().getFullYear(),
      label: String(new Date().getFullYear()),
    },
  })

  const {
    getTaxResolutionDetails,
    saveTaxResolutionDetails,
    publishTaxResolutionDetails,
  } = useFourThreeThreeBApi()

  const applyValuesToForm = useCallback(
    (formInstance: any, valuesToApply: Record<string, any>) => {
      if (!formInstance) return
      Object.entries(valuesToApply).forEach(([fieldName, val]) => {
        try {
          const field = formInstance.getField(fieldName)
          const type = getFieldTypeString(field)

          switch (type) {
            case 'PDFTextField': {
              const maxLen = (field as PDFTextField).getMaxLength()
              let text = val !== null && val !== undefined ? String(val) : ''
              if (maxLen !== undefined && text.length > maxLen) {
                text = text.slice(0, maxLen)
              }
              ;(field as PDFTextField).setText(text)
              break
            }
            case 'PDFCheckBox':
              val ? field.check() : field.uncheck()
              break
            case 'PDFRadioGroup':
            case 'PDFDropdown':
              if (val) field.select(String(val))
              break
            case 'PDFOptionList':
              if (val) {
                const listVal = Array.isArray(val) ? val.map(String) : [String(val)]
                field.select(listVal)
              }
              break
          }
        } catch {
          /* Ignore missing fields */
        }
      })
    },
    []
  )

  const fetchTaxResolutionDetails = useCallback((): Promise<any | null> => {
    const getlang = localStorage.getItem('i18nextLng')
    const lang =
      getlang === 'en' || getlang === 'en-US' || getlang === 'en-us' ? 'en-US' : 'es'
    const params = {
      month: filterParams.month.value,
      year: filterParams.year.value,
      form_type: formType,
      branch_id: branchData?.id,
      lang,
    }

    return new Promise((resolve) => {
      getTaxResolutionDetails(
        params,
        (_message: string, resp: any) => {
          const details = resp?.data || resp
          resolve(details && typeof details === 'object' ? details : null)
        },
        () => {
          resolve(null)
        }
      )
    })
  }, [
    filterParams.month.value,
    filterParams.year.value,
    formType,
    branchData?.id,
    getTaxResolutionDetails,
  ])

  const cloneBytes = (bytes: Uint8Array): Uint8Array => new Uint8Array(bytes)

  const getFieldLabel = (field: any): string => {
    try {
      const alt = field.acroField.getAlternateName()
      if (alt && alt.asString().trim() !== '') return alt.asString().trim()
    } catch (e) {
      /* no-op */
    }

    const rawName = field.getName()
    const parts = rawName.split(/[./]/)
    const clean = parts[parts.length - 1]
      .replace(/\[\d+\]/g, '')
      .replace(/[-_]/g, ' ')
      .trim()
    return clean || rawName
  }

  const getFieldOptions = (field: any, type: string) => {
    try {
      if (
        type === 'PDFDropdown' ||
        type === 'PDFOptionList' ||
        type === 'PDFRadioGroup'
      ) {
        return field.getOptions()
      }
    } catch {
      /* no-op */
    }
    return []
  }

  const getInitialValue = (field: any, type: string) => {
    try {
      switch (type) {
        case 'PDFTextField':
          return field.getText() || ''
        case 'PDFCheckBox':
          return field.isChecked()
        case 'PDFRadioGroup':
          return field.getSelected() || ''
        case 'PDFDropdown':
          return field.getSelected()?.[0] || ''
        case 'PDFOptionList':
          return field.getSelected() || []
        default:
          return ''
      }
    } catch {
      return ''
    }
  }

  const loadPdfFromUrl = useCallback(
    async (
      formUrl: string,
      revisionLabel: string | null,
      savedFieldValues: Record<string, any> | null | undefined,
      token: number
    ) => {
      const directUrl = normalizePdfUrl(formUrl)
      const response = await fetch(directUrl)

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF. Status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('text/html')) {
        throw new Error(
          `Expected a PDF file, but received an HTML webpage. Check if the URL is a preview page instead of a raw download link.`
        )
      }

      const buffer = await response.arrayBuffer()
      if (token !== loadTokenRef.current) return

      const masterBytes = new Uint8Array(buffer)

      const isPdfHeader =
        masterBytes[0] === 0x25 &&
        masterBytes[1] === 0x50 &&
        masterBytes[2] === 0x44 &&
        masterBytes[3] === 0x46

      if (!isPdfHeader) {
        throw new Error('Downloaded file does not contain a valid %PDF header.')
      }

      masterBytesRef.current = masterBytes

      // STEP 1: PDF.JS
      const pdfjsDoc = await pdfjs.getDocument({
        data: cloneBytes(masterBytes),
      }).promise

      if (token !== loadTokenRef.current) {
        await pdfjsDoc.destroy()
        return
      }

      const meta: Record<number, any> = {}
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const page = await pdfjsDoc.getPage(i)
        const unscaledViewport = page.getViewport({ scale: 1 })
        const baseScale = PAGE_WIDTH / unscaledViewport.width
        meta[i - 1] = { unscaledViewport, baseScale }
      }

      const pageCount = pdfjsDoc.numPages
      await pdfjsDoc.destroy()
      if (token !== loadTokenRef.current) return

      setRevision(revisionLabel)
      setPageMeta(meta)
      setNumPages(pageCount)

      // STEP 2: PDF-LIB
      const pdfDoc = await PDFDocument.load(cloneBytes(masterBytes))
      const form = pdfDoc.getForm()
      const libPages = pdfDoc.getPages()
      const fields = form.getFields()

      const findPageIndex = (widgetDict: any) => {
        for (let i = 0; i < libPages.length; i++) {
          const annots = libPages[i].node.Annots()
          if (!annots) continue
          for (let j = 0; j < annots.size(); j++) {
            if (pdfDoc.context.lookup(annots.get(j)) === widgetDict) return i
          }
        }
        return -1
      }

      const defs: any[] = []
      const initialValues: Record<string, any> = {}

      fields.forEach((field) => {
        const name = field.getName()
        const type = getFieldTypeString(field)
        const maxLength =
          field instanceof PDFTextField ? field.getMaxLength() : undefined
        field.acroField.getWidgets().forEach((widget) => {
          const pageIndex = findPageIndex(widget.dict)
          if (pageIndex === -1) return

          defs.push({
            key: `${name}-${pageIndex}-${widget.getRectangle().x}-${widget.getRectangle().y}`,
            name,
            label: getFieldLabel(field),
            type,
            pageIndex,
            rect: widget.getRectangle(),
            maxLength,
            options: getFieldOptions(field, type),
          })
        })

        initialValues[name] = getInitialValue(field, type)
      })

      // STEP 3: Normalize & Merge Saved API Values
      let savedValuesNormalized: Record<string, any> = {}

      if (savedFieldValues) {
        const fieldNameByLower = new Map<string, string>()
        fields.forEach((f) =>
          fieldNameByLower.set(f.getName().toLowerCase(), f.getName())
        )

        Object.entries(savedFieldValues).forEach(([savedKey, val]) => {
          if (savedKey in initialValues) {
            savedValuesNormalized[savedKey] = val
          } else {
            const realName = fieldNameByLower.get(savedKey.toLowerCase())
            if (realName) savedValuesNormalized[realName] = val
          }
        })
      }

      const currentValues = { ...initialValues, ...savedValuesNormalized }

      // STEP 4: Pre-fill Priority
      const finalValues = preFillAnyForm(
        formType,
        currentValues,
        branchData,
        userData
      )

      // STEP 5: Apply & Save
      applyValuesToForm(form, finalValues)
      pdfDocRef.current = pdfDoc
      formRef.current = form
      setFieldDefs(defs)
      setValues(finalValues)
      setDocumentFile({ data: cloneBytes(masterBytes) })

      generatedPeriodRef.current = {
        month: filterParams.month.label,
        year: filterParams.year.label,
      }
    },
    [
      formType,
      filterParams.month.label,
      filterParams.year.label,
      branchData,
      userData,
      applyValuesToForm,
    ]
  )

  const loadForm = useCallback(async () => {
    const lang = localStorage.getItem('i18nextLng')
    const staticFallbackUrl =
      lang !== 'es' ? FormsListEng[formType] : FormsListSn[formType]

    const token = ++loadTokenRef.current

    setLoading(true)
    setIsLoading(true)
    setHasError(false)

    pdfDocRef.current = null
    formRef.current = null
    masterBytesRef.current = null
    detailsRef.current = null
    setDocumentFile(null)
    setNumPages(0)
    setFieldDefs([])
    setValues({})
    setRevision(null)
    setIsLatestLoaded(false)
    setNewerVersionAvailable(false)
    setPageMeta({})

    try {
      const details = await fetchTaxResolutionDetails()
      if (token !== loadTokenRef.current) return
      detailsRef.current = details

      const formUrl = details?.data?.pdf_url || staticFallbackUrl
      if (!formUrl) {
        console.warn('No form URL available for', formType)
        setHasError(true)
        return
      }

      const revisionLabel = details?.data?.form_revision ?? null

      await loadPdfFromUrl(
        formUrl,
        revisionLabel,
        details?.data?.field_value,
        token
      )
      if (token !== loadTokenRef.current) return

      setIsLatestLoaded(false)
      setIsPublished(!!details?.data?.is_published)
      setNewerVersionAvailable(!!details?.data?.has_newer_version)
    } catch (err) {
      if (token === loadTokenRef.current) {
        setHasError(true)
        toast.error(t('failed_to_load_pdf') || 'Failed to load PDF form.')
      }
    } finally {
      if (token === loadTokenRef.current) {
        setLoading(false)
        setIsLoading(false)
      }
    }
  }, [formType, fetchTaxResolutionDetails, loadPdfFromUrl, t])

  const loadLatestVersion = useCallback(async () => {
    const details = detailsRef.current
    if (!details?.data?.latest_pdf_url) return

    const token = ++loadTokenRef.current
    setIsLoading(true)
    setHasError(false)

    try {
      await loadPdfFromUrl(
        details?.data.latest_pdf_url,
        details?.data.latest_revision ?? null,
        details?.data.field_value,
        token
      )
      if (token !== loadTokenRef.current) return

      setIsLatestLoaded(true)
      setNewerVersionAvailable(false)
    } catch (err) {
      if (token === loadTokenRef.current) {
        setHasError(true)
        toast.error(t('failed_to_load_pdf') || 'Failed to load latest version.')
      }
    } finally {
      if (token === loadTokenRef.current) {
        setIsLoading(false)
      }
    }
  }, [loadPdfFromUrl, t])

  useEffect(() => {
    loadForm()
  }, [formType, branchData?.id, filterParams.month.value, filterParams.year.value])

  const updateField = (name: string, type: string, value: any) => {
    const form = formRef.current
    let sanitizedValue = value

    if (type === 'PDFTextField' && form) {
      try {
        const field = form.getField(name) as PDFTextField
        const maxLen = field.getMaxLength()
        if (
          maxLen !== undefined &&
          typeof value === 'string' &&
          value.length > maxLen
        ) {
          sanitizedValue = value.slice(0, maxLen)
        }
      } catch {
        /* ignore */
      }
    }

    setValues((prev) => ({ ...prev, [name]: sanitizedValue }))
    if (!form) return
    setIsDirty(true)

    try {
      const field = form.getField(name)
      const fieldType = getFieldTypeString(field)

      switch (fieldType) {
        case 'PDFTextField':
          ;(field as PDFTextField).setText(
            sanitizedValue !== null && sanitizedValue !== undefined
              ? String(sanitizedValue)
              : ''
          )
          break
        case 'PDFCheckBox': {
          const cb = field as PDFCheckBox
          sanitizedValue ? cb.check() : cb.uncheck()
          break
        }
        case 'PDFRadioGroup':
          ;(field as PDFRadioGroup).select(sanitizedValue)
          break
        case 'PDFDropdown':
          ;(field as PDFDropdown).select(sanitizedValue)
          break
        case 'PDFOptionList':
          ;(field as PDFOptionList).select(sanitizedValue)
          break
      }
    } catch (err) {
      /* ignore */
    }
  }

  const udpateFormDetails = async () => {
    setIsLoading(true)
    const getLang = localStorage.getItem('i18nextLng')
    const lang =
      getLang === 'en' || getLang === 'en-US' || getLang === 'en-us' ? 'en-US' : 'es'

    const params = {
      form_revision: revision,
      field_value: values,
      month: filterParams.month.value,
      year: filterParams.year.value,
      form_type: formType,
      branch_id: branchData?.id,
      generated_at: new Date().toISOString(),
      lang,
    }
    saveTaxResolutionDetails(
      params,
      (_message: string, resp: any) => {
        toast.success(resp?.message || t('form_saved_successfully'))
        setIsLoading(false)
        setIsDirty(false)
      },
      (error: any) => {
        toast.error(error || t('something_went_wrong_publishing'))
        setIsLoading(false)
        setIsDirty(false)
      }
    )
  }

  const saveAndPublishTaxResolution = async () => {
    setIsLoading(true)
    const getLang = localStorage.getItem('i18nextLng')
    const lang =
      getLang === 'en' || getLang === 'en-US' || getLang === 'en-us' ? 'en-US' : 'es'

    const params = {
      form_revision: revision,
      month: filterParams.month.value,
      year: filterParams.year.value,
      form_type: formType,
      branch_id: branchData?.id,
      generated_at: new Date().toISOString(),
      lang,
    }
    const saveParams = {
      form_revision: revision,
      field_value: values,
      month: filterParams.month.value,
      year: filterParams.year.value,
      form_type: formType,
      branch_id: branchData?.id,
      generated_at: new Date().toISOString(),
      lang,
    }
    saveTaxResolutionDetails(
      saveParams,
      (_message: string, resp: any) => {
        publishTaxResolutionDetails(
          params,
          (_message: string, resp: any) => {
            setIsPublished(!!resp?.data?.data?.is_published)
            toast.success(resp?.message || t('form_published_successfully'))
            setIsLoading(false)
            setPublishAlert(false)
            setIsDirty(false)
          },
          (error: any) => {
            toast.error(error || t('something_went_wrong_publishing'))
            setIsLoading(false)
            setPublishAlert(false)
            setIsDirty(false)
          }
        )
      },
      (error: any) => {
        toast.error(error || t('something_went_wrong_publishing'))
        setIsLoading(false)
        setIsDirty(false)
      }
    )
  }

  const downloadPdf = async () => {
    if (!pdfDocRef.current) return
    const bytes: any = await pdfDocRef.current.save()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${formType || 'form'}.pdf`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('form_downloaded_successfully'))
  }

  const getViewport = (pageIndex: number) => {
    const meta = pageMeta[pageIndex]
    if (!meta) return null
    return meta.unscaledViewport.clone({ scale: meta.baseScale * zoom })
  }

  const rectToStyle = (def: any) => {
    const viewport = getViewport(def.pageIndex)
    if (!viewport) return { display: 'none' }

    const { x, y, width, height } = def.rect
    const [sx1, sy1] = viewport.convertToViewportPoint(x, y)
    const [sx2, sy2] = viewport.convertToViewportPoint(x + width, y + height)

    return {
      position: 'absolute' as const,
      left: Math.min(sx1, sx2),
      top: Math.min(sy1, sy2),
      width: Math.abs(sx2 - sx1),
      height: Math.abs(sy2 - sy1),
    }
  }

  const renderOverlay = (def: any) => {
    const { name, type, maxLength } = def
    const baseStyle: React.CSSProperties = {
      ...rectToStyle(def),
      border: '1px solid transparent',
      background: 'transparent',
      fontSize: 11,
      zIndex: 10,
      pointerEvents: 'auto',
      borderRadius: '2px',
      color: '#000',
    }

    switch (type) {
      case 'PDFTextField':
        return (
          <input
            key={def.key}
            value={values[name] ?? ''}
            onChange={(e) => {
              updateField(name, type, e.target.value)
            }}
            maxLength={maxLength}
            style={baseStyle}
            disabled={isPublished}
          />
        )

      case 'PDFCheckBox':
        return (
          <input
            key={def.key}
            type="checkbox"
            checked={!!values[name]}
            onChange={(e) => {
              updateField(name, type, e.target.checked)
            }}
            style={{
              ...baseStyle,
              background: 'transparent',
              cursor: 'pointer',
              color: '#000',
            }}
            disabled={isPublished}
          />
        )

      case 'PDFRadioGroup':
      case 'PDFDropdown':
        return (
          <select
            key={def.key}
            value={values[name] ?? ''}
            onChange={(e) => updateField(name, type, e.target.value)}
            style={baseStyle}
            disabled={isPublished}
          >
            <option value="">--</option>
            {def.options.map((opt: string) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )

      case 'PDFOptionList':
        return (
          <select
            key={def.key}
            multiple
            value={values[name] ?? []}
            onChange={(e) =>
              updateField(
                name,
                type,
                Array.from(e.target.selectedOptions).map((o) => o.value)
              )
            }
            style={baseStyle}
            disabled={isPublished}
          >
            {def.options.map((opt: string) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )

      default:
        return null
    }
  }

  const handleFilterChange = (value: any, type: 'month' | 'year') => {
    setFilterParams((prev) => ({
      ...prev,
      [type]: value,
    }))
  }

  const ready =
    !loading &&
    !hasError &&
    numPages > 0 &&
    Object.keys(pageMeta).length === numPages &&
    !!documentFile

  if (formType === 'f433b') {
    return <Navigate to={URLS.TAX_FORM_433B} replace />
  }
  if (!formType || !VALID_FORM_TYPES.has(formType)) {
    return <Navigate to={URLS.PAGE_NOT_FOUND} replace />
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap pb-1 pt-2">
          <div>
            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
              {getFormTitle(formType)}
            </h1>
          </div>
          {formType !== 'p5059' && formType !== 'p1854' && (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* General Form Generation Filter (Month & Year) */}
              <Box className="filter-boxes px-2 py-1 rounded-3 bg-white shadow-sm border">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="small text-black fw-bold">
                    <i className="fa-solid fa-clock-rotate-left text-primary me-1"></i>
                    {t('taxPeriodHeader', 'Tax Period / Resolution Filing')}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {/* Month Selector */}
                  <div className="filter-box" style={{ minWidth: '130px' }}>
                    <Select
                      onChange={(e: any) => handleFilterChange(e, 'month')}
                      options={monthList}
                      defaultValue={filterParams.month}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  {/* Year Selector */}
                  <div className="filter-box" style={{ minWidth: '100px' }}>
                    <Select
                      onChange={(e: any) => handleFilterChange(e, 'year')}
                      options={lastYears(5)}
                      defaultValue={filterParams.year}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  {/* Generate Form Trigger */}
                  <div className="filter-box">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => loadForm()}
                      endIcon={<i className="fa-solid fa-circle-arrow-right"></i>}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                      }}
                    >
                      {t('generateForm')}
                    </Button>
                  </div>
                </div>
              </Box>

              {!isLoading && (
                <Box className="filter-boxes px-2 py-1 rounded-3 bg-white shadow-sm border">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small text-black fw-bold">
                      <i className="fa-solid fa-file-circle-check text-primary me-1"></i>
                      {t('formActionsHeader', 'Form Actions')}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {!isPublished && !isLoading && (
                      <>
                        <div className="filter-box">
                          <button
                            type="button"
                            onClick={udpateFormDetails}
                            className="generate-btn-modern"
                            disabled={!ready}
                          >
                            {t('save')}
                          </button>
                        </div>

                        <div className="filter-btn ms-auto">
                          <button
                            type="button"
                            onClick={() => setPublishAlert(true)}
                            className="button-tertiary"
                          >
                            <img src={publishBudgetIcon} alt="" />
                            {t('save_publish')}
                          </button>
                        </div>
                      </>
                    )}

                    {isPublished && !isLoading && (
                      <Button
                        startIcon={<i className="fa-solid fa-file-pdf"></i>}
                        variant="outlined"
                        color="success"
                        size="medium"
                        onClick={downloadPdf}
                        disabled={!ready}
                        title={t('download_pdf')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'bold',
                        }}
                      >
                        {t('download_pdf')}
                      </Button>
                    )}
                  </div>
                </Box>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="filter-btn">
        <div className={`page-loader ${loading || isLoading ? 'visible' : ''}`}>
          <div className="loader"></div>
        </div>
      </div>
      <div
        className="executive-dashboard p-0"
        style={{ maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}
        ref={tableContainerRef}
      >
        <div className="position-absolute top-10 end-1 m-1 z-3 d-flex align-items-center gap-2">
          {revision && (
            <>
              <span className="text-primary px-2 text-black">
                Rev: <strong>{revision}</strong>
                {isLatestLoaded && ` (${t('latest')})`}
              </span>
              {newerVersionAvailable && !isPublished && (
                <Button
                  variant="text"
                  size="small"
                  onClick={loadLatestVersion}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    color: '#0d6efd',
                    fontWeight: 'bold',
                  }}
                >
                  {t('new_revision_available')}
                </Button>
              )}
            </>
          )}
        </div>
        <div
          style={{
            padding: 0,
            margin: 0,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {hasError && !loading && !isLoading && (
            <div className="p-4 text-center">
              <p className="text-danger fw-bold">{t('failed_to_load_pdf')}</p>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-2"
                onClick={() => loadForm()}
              >
                {t('retry') || 'Retry'}
              </button>
            </div>
          )}

          {ready && !hasError && (
            <Document
              file={documentFile}
              loading={
                <PdfLoadingView text={t('please_wait', 'Please wait...')} />
              }
              error={
                <PdfErrorView
                  title={t('failed_to_load_pdf', 'Failed to load PDF.')}
                  subtitle={t(
                    'pdf_render_error_sub',
                    'The document could not be rendered. Click retry to attempt reloading.'
                  )}
                  retryBtnText={t('retry')}
                  onRetry={() => loadForm()}
                />
              }
            >
              {Array.from(new Array(numPages), (_, pageIndex) => {
                const viewport = getViewport(pageIndex)
                const meta = pageMeta[pageIndex]
                if (!viewport) return null
                return (
                  <div
                    key={pageIndex}
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: viewport.width,
                      height: viewport.height,
                      margin: '0 auto 16px auto',
                    }}
                  >
                    <Page
                      pageNumber={pageIndex + 1}
                      scale={meta.baseScale * zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />

                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 10,
                      }}
                    >
                      {fieldDefs
                        .filter((def) => def.pageIndex === pageIndex)
                        .map((def) => renderOverlay(def))}
                    </div>
                  </div>
                )
              })}
            </Document>
          )}
        </div>
        <ScrollToggle scrollRef={tableContainerRef} />
      </div>

      <SAlert
        msg={t('publish_confirmation', { formTitle: getFormTitle(formType) })}
        show={publishAlert}
        title={t('are_you_sure')}
        onConfirm={() => {
          saveAndPublishTaxResolution()
        }}
        confirmBtnText={t('yes_publish')}
        cancelBtnText={t('cancel')}
        showCancel={true}
        onCancel={() => setPublishAlert(false)}
      />
      <NavigationBlockerDialog
        open={showPrompt}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </div>
  )
}